/**
 * Servicio de API para autenticación con el backend real
 * Maneja las peticiones HTTP para login y autenticación
 */

import { API_BASE_URL } from '../config/api'

class AuthApiService {
  constructor() {
    this.baseURL = API_BASE_URL
  }

  /**
   * Realiza login con credenciales contra la API real
   * @param {string} nombreUsuario - Nombre de usuario
   * @param {string} contrasena - Contraseña del usuario
   * @returns {Promise} Respuesta de la API
   */
  async login(nombreUsuario, contrasena) {
    try {
      const response = await fetch(`${this.baseURL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          nombre_usuario: nombreUsuario,
          contrasena: contrasena
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || errorData.mensaje || 'Error de autenticación')
      }

      const data = await response.json()

      // 🔍 LOGS DE DEBUG - Respuesta de login
      console.log('🔐 [authApi] ========================================')
      console.log('🔐 [authApi] Login exitoso')
      console.log('🔐 [authApi] Usuario recibido del backend:', data.usuario)
      console.log('🔐 [authApi] Usuario.plan:', data.usuario?.plan)
      console.log('🔐 [authApi] Usuario.plan.subtype:', data.usuario?.plan?.subtype)
      console.log('🔐 [authApi] Usuario.role:', data.usuario?.role)

      // Validar estructura de respuesta esperada
      if (!data.token || !data.usuario) {
        throw new Error('Respuesta inválida del servidor')
      }

      return {
        success: true,
        data: {
          token: data.token,
          mensaje: data.mensaje,
          usuario: data.usuario
        }
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Error de conexión'
      }
    }
  }

  /**
   * Verifica si el token es válido verificando su estructura y expiración
   * @param {string} token - Token JWT
   * @returns {Promise} Resultado de validación
   */
  async validateToken(token) {
    try {
      if (!token) {
        return { success: false, error: 'Token no proporcionado' }
      }

      // Verificar estructura del token JWT
      const parts = token.split('.')
      if (parts.length !== 3) {
        return { success: false, error: 'Token inválido' }
      }

      // Decodificar payload
      const payload = JSON.parse(atob(parts[1]))
      const currentTime = Date.now() / 1000

      // Verificar expiración
      if (payload.exp && payload.exp < currentTime) {
        return { success: false, error: 'Token expirado' }
      }

      return {
        success: true,
        data: { 
          valid: true, 
          payload: payload,
          expiresAt: new Date(payload.exp * 1000)
        }
      }
    } catch (error) {
      console.error('Error validating token:', error)
      return {
        success: false,
        error: error.message
      }
    }
  }

  /**
   * Refresh token (no implementado en backend, retorna error)
   * @param {string} token - Token actual
   * @returns {Promise} Nuevo token
   */
  async refreshToken(token) {
    console.warn('Refresh token no está implementado en el backend')
    return {
      success: false,
      error: 'Refresh token no disponible'
    }
  }

  /**
   * Logout del usuario con endpoint real del backend
   * @param {string} token - Token del usuario
   * @returns {Promise} Resultado del logout
   */
  async logout(token) {
    try {
      const response = await fetch(`${this.baseURL}/auth/logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      })

      if (!response.ok) {
        const errorData = await response.json()
        // Aunque falle el logout del backend, continuamos con la limpieza local
        return {
          success: true,
          data: { message: 'Logout local completado (error en servidor)' }
        }
      }

      const data = await response.json()

      return {
        success: true,
        data: { message: data.mensaje || 'Logout exitoso' }
      }

    } catch (error) {
      // En caso de error, al menos limpiamos localmente
      return {
        success: true,
        data: { message: 'Logout local completado (error de conexión)' }
      }
    }
  }
}

// Exportar instancia singleton
export const authApiService = new AuthApiService()